import { Flex, Group, Stack } from '@mantine/core';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgentDashboard } from '../../../../api/agent-miner-client/agent-miner-client.hooks';
import { BrandedLoadingOverlay } from '../../../../components/loader/branded-loader';
import { StyledButton } from '../../../../containers/financials/charts/shared/chart-display-type-button-set';
import { exportChart } from '../../../../helpers/image-downloader/image-downloader';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';
import { setAgentChartType } from '../../../../store/process-analysis-store/process-analysis-store.actions';
import { useTimeAllocation } from '../../../../store/process-analysis-store/process-analysis-store.hooks';
import { baseWhite } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { useTimeAllocationsData } from '../../hooks/measure-comparison';
import { CycleTimeChart } from './cycle-time-chart';
import { HistogramChart } from './histogram-chart';
import { UsageChart } from './usage-chart';

export const MainSection = () => {
  const i18n = useTranslation();
  const chartRef = useRef<HTMLDivElement>(null);
  const agentViewMode = useProcessAnalysisStore((state) => state.agentViewMode);
  const agentChartType = useProcessAnalysisStore((state) => state.agentChartType);

  const organizationId = useGlobalStore((state) => state.organization?.id);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const startDate = useProcessAnalysisStore((state) => state.startDate);
  const endDate = useProcessAnalysisStore((state) => state.endDate);
  const selectedTaskType = useProcessAnalysisStore((state) => state.selectedTaskType);
  const selectedPoints = useProcessAnalysisStore((state) => state.selectedPoints);
  const timeAllocationType = useTimeAllocation();

  const { data, query } = useAgentDashboard({
    organization_id: organizationId ?? '',
    portfolio_id: portfolioId ?? '',
    start_date: startDate,
    end_date: endDate,
    time_allocation_type: timeAllocationType,
    ...(selectedTaskType ? { task_type: selectedTaskType } : {}),
    ...(selectedPoints != null ? { points: selectedPoints } : {}),
  });

  // Fetch time allocation buckets into the store so the charts can label
  // weekly/biweekly buckets with their full window (see getTimeAllocationChartLabels).
  const { isLoading: isTimeAllocationsLoading } = useTimeAllocationsData();

  const handleExport = async () => {
    if (chartRef.current) {
      try {
        await exportChart(chartRef.current, `agents_${agentViewMode}_chart.png`);
      } catch (error) {
        console.error('Failed to export chart:', error);
      }
    }
  };

  const chart = useMemo(() => {
    if (agentViewMode === 'usage') {
      return <UsageChart />;
    }

    if (agentChartType === 'line') {
      return <CycleTimeChart />;
    }

    return (
      <HistogramChart
        histogram={data?.cycle_time_analysis?.histogram ?? []}
        agentMedian={data?.cycle_time_analysis?.agent?.median_cycle_time_days ?? null}
        nonAgentMedian={data?.cycle_time_analysis?.non_agent?.median_cycle_time_days ?? null}
      />
    );
  }, [
    agentViewMode,
    agentChartType,
    data?.cycle_time_analysis?.histogram,
    data?.cycle_time_analysis?.agent?.median_cycle_time_days,
    data?.cycle_time_analysis?.non_agent?.median_cycle_time_days,
  ]);

  return (
    <Stack h="100%" style={{ overflow: 'hidden', position: 'relative' }}>
      <BrandedLoadingOverlay
        visible={query.isLoading || isTimeAllocationsLoading}
        variant="colored"
        transitionDuration={30}
      />
      <Flex justify="space-between" align="center">
        <Group>
          {agentViewMode === 'cycle_time' ? (
            <>
              <StyledButton
                label="Histogram"
                iconName="bar_chart"
                onClick={() => setAgentChartType('histogram')}
                selected={agentChartType === 'histogram'}
              />
              <StyledButton
                label="Line Chart"
                iconName="stacked_line_chart"
                onClick={() => setAgentChartType('line')}
                selected={agentChartType === 'line'}
              />
            </>
          ) : (
            <>
              <StyledButton
                label="Line Chart"
                iconName="stacked_line_chart"
                onClick={() => setAgentChartType('line')}
                selected={agentChartType === 'line'}
              />
              <StyledButton
                label="Bar Chart"
                iconName="bar_chart"
                onClick={() => setAgentChartType('bar')}
                selected={agentChartType === 'bar'}
              />
            </>
          )}
        </Group>
        <Button
          variant="secondary"
          radius="xl"
          size="xs"
          leftSection={
            <Icon name="download" color={baseWhite} variant="filled" size={16} style={{ cursor: 'pointer' }} />
          }
          onClick={handleExport}
        >
          {i18n.t('common.actions.export')}
        </Button>
      </Flex>
      <div ref={chartRef} style={{ flex: 1, minHeight: 400 }}>
        {chart}
      </div>
    </Stack>
  );
};
